import { isEqual } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';
import Markets from '@/constants/Markets';
import Languages from '@/constants/Languages';
import { GameStatuses } from '@/constants/GameStatuses';
import { Volatilities } from '@/constants/Volatilities';
import { FileTypesCompliance } from '@/constants/FileTypesCompliance';
import { FileTypesMarketing } from '@/constants/FileTypesMarketing';
import { AppFilters, Config } from '@/lib/types';
import dayjs from 'dayjs';

export type FilterSchema = {
  navigation: boolean;
  market: number[];
  fileTypeCompliance: string[];
  fileFormatCompliance: string[];
  fileTypeMarketing: string[];
  fileFormatMarketing: string[];
  partnerType: string[];
  platform: string[];
  operator: string[];
  brand: string[];
  volatility: string[];
  language: string[];
  releaseDate: string[];
  rtp: string[][];
  gameFamily: string[];
  gameStatus: string[];
  gameFeatures: string[];
  gameGroups: string[];
  gameProviders: string[];
};

export type InitialFilterValues = Partial<
  Omit<FilterSchema, 'navigation'>
>;

export type FilterDetails<
  T = string | number | [number, number] | string[],
> = {
  value: T[];
  label: ReactNode;
  defaultValue: T[];
  setter: (v: T[]) => void;
};

export type ResourcesSidebarSections =
  | 'navigation'
  | ResourcesSidebarFilters;

export type ResourcesSidebarFilters =
  | 'market'
  | 'language'
  | 'gameStatus'
  | 'volatility'
  | 'gameFeatures'
  | 'gameGroups'
  | 'gameProviders'
  | 'fileTypeCompliance'
  | 'fileFormatCompliance'
  | 'rtp'
  | 'fileTypeMarketing'
  | 'fileFormatMarketing'
  | 'releaseDate'
  | 'gameFamily'
  | 'partnerType'
  | 'platform'
  | 'operator'
  | 'brand';

type ResourcesSubpage =
  | 'resources'
  | 'games'
  | 'compliance'
  | 'marketing';

export const useSectionsConfig = () => {
  const location = useLocation();
  const subpath = (location.pathname.substring(
    location.pathname.lastIndexOf('/') + 1,
  ) || '') as ResourcesSubpage;

  const visibleSections = useMemo(() => {
    const sections: {
      name: ResourcesSidebarSections;
      pages?: ResourcesSubpage[];
    }[] = [
      { name: 'navigation' },
      {
        name: 'market',
        pages: ['compliance', 'games'],
      },
      {
        name: 'gameStatus',
        pages: ['games'],
      },
      {
        name: 'releaseDate',
        pages: ['games'],
      },
      {
        name: 'volatility',
        pages: ['games'],
      },
      {
        name: 'gameFeatures',
        pages: ['games'],
      },
      {
        name: 'gameGroups',
        pages: ['games', 'marketing'],
      },
      {
        name: 'gameProviders',
        pages: ['games'],
      },
      {
        name: 'fileTypeCompliance',
        pages: ['compliance'],
      },
      {
        name: 'fileFormatCompliance',
        pages: ['compliance'],
      },
      {
        name: 'rtp',
        pages: ['games'],
      },
      {
        name: 'fileTypeMarketing',
        pages: ['marketing'],
      },
      {
        name: 'fileFormatMarketing',
        pages: ['marketing'],
      },
      {
        name: 'gameFamily',
        pages: ['games'],
      },
      {
        name: 'language',
        pages: ['games', 'compliance'],
      },
    ];

    const result = sections
      .filter(
        (section) =>
          !section.pages || section.pages?.includes(subpath),
      )
      .reduce(
        (acc, section) => {
          acc[section.name] = true;
          return acc;
        },
        {} as Record<ResourcesSidebarSections, boolean>,
      );

    return {
      ...result,
      lessMarkets:
        subpath === 'compliance' || subpath === 'marketing',
    };
  }, [subpath]);
  return visibleSections;
};

export const getVisibleFilters = (
  filters?: Record<
    ResourcesSidebarSections,
    string | string[] | number[]
  >,
  visibleSections?: Record<ResourcesSidebarSections, boolean>,
) =>
  Object.entries(visibleSections || {}).reduce(
    (acc, section) => {
      const [sectionName, isVisible] = section as [
        ResourcesSidebarSections,
        boolean,
      ];
      if (isVisible && filters?.[sectionName]) {
        acc[sectionName] = filters[sectionName];
      }
      return acc;
    },
    {} as Record<
      ResourcesSidebarSections,
      string | string[] | number[]
    >,
  );

export function getFilteredFiltersWithDetails(
  filterWithDetails: Record<
    ResourcesSidebarFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FilterDetails<any>
  >,
  visibleSections: Partial<Record<ResourcesSidebarSections, boolean>>,
) {
  return Object.entries(filterWithDetails).reduce(
    (acc, [key, filter]) => {
      const section = key as ResourcesSidebarSections;
      if (
        filter.value &&
        !isEqual(filter.value, filter.defaultValue) &&
        visibleSections?.[section]
      ) {
        acc[section] = filter;
      }
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {} as Record<ResourcesSidebarSections, FilterDetails<any>>,
  );
}

export function getLabelForFilterValue(
  section: ResourcesSidebarFilters,
  value: string | number | string[] | [number, number],
  filtersData?: AppFilters,
  configData?: Config,
): ReactNode {
  switch (section) {
    case 'market': {
      if (typeof value === 'number' && filtersData?.markets) {
        const market = filtersData.markets.find(
          (m) => m.market_id === value,
        );
        if (market) {
          const marketConfig = Markets[market.market_name];
          return marketConfig?.label || market.market_name;
        }
      }
      return value;
    }

    case 'language': {
      if (typeof value === 'string') {
        const language = Languages[value];
        return language?.label || value;
      }
      return value;
    }

    case 'gameStatus': {
      if (typeof value === 'string') {
        const status =
          GameStatuses[
            value.toLowerCase() as keyof typeof GameStatuses
          ];
        return status?.label || value;
      }
      return value;
    }

    case 'volatility': {
      if (Array.isArray(value) && value.length === 2) {
        const [min, max] = value;
        const volatility = Volatilities.find(
          (v) => v.value[0] === min && v.value[1] === max,
        );
        return volatility?.name || 'Custom';
      }
      return value;
    }

    case 'fileTypeCompliance': {
      if (typeof value === 'string') {
        const fileType =
          FileTypesCompliance[
            value as keyof typeof FileTypesCompliance
          ];
        return fileType?.label || value;
      }
      return value;
    }

    case 'fileTypeMarketing': {
      if (typeof value === 'string') {
        const fileType =
          FileTypesMarketing[
            value as keyof typeof FileTypesMarketing
          ];
        return fileType?.label || value;
      }
      return value;
    }

    case 'releaseDate': {
      if (typeof value === 'string') {
        return dayjs(value).format('YYYY MMM DD');
      }
      return value;
    }

    case 'rtp': {
      if (
        Array.isArray(value) &&
        value.length === 2 &&
        configData?.filters.rtps
      ) {
        const rtpItem = configData.filters.rtps.find(
          (r) => r.value[0] === value[0] && r.value[1] === value[1],
        );
        return rtpItem?.name || value;
      }
      return value;
    }

    default:
      return value;
  }
}
